import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.ts';
import type { Space } from '../lib/scale.ts';

export type BoxSurface = 'none' | 'base' | 'muted' | 'raised';
export type BoxRadius = 'none' | 'control' | 'card';

const paddings: Record<Space, string> = {
  none: 'p-0',
  xs: 'p-1',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-10',
};

const surfaces: Record<BoxSurface, string> = {
  none: '',
  base: 'bg-surface',
  muted: 'bg-surface-muted',
  raised: 'bg-surface-raised',
};

const radii: Record<BoxRadius, string> = {
  none: '',
  control: 'rounded-control',
  card: 'rounded-card',
};

export interface BoxProps extends ComponentProps<'div'> {
  padding?: Space;
  surface?: BoxSurface;
  radius?: BoxRadius;
  border?: boolean;
}

/**
 * A plain surface: padding, background, radius, optional hairline. Where `Card`
 * is a component with a header contract, `Box` is the neutral container you
 * reach for when the thing on screen is not a card.
 */
export function Box({
  padding = 'md',
  surface = 'none',
  radius = 'none',
  border = false,
  className,
  ...props
}: BoxProps) {
  return (
    <div
      className={cn(
        paddings[padding],
        surfaces[surface],
        radii[radius],
        border && 'border border-border-subtle',
        className,
      )}
      {...props}
    />
  );
}
